
import { useState } from 'react';
import DownloadModal from '../../../components/DownloadModal';

type Feature =
  | { type: 'item'; text: string }
  | { type: 'inherited'; text: string }
  | { type: 'free'; text: string }
  | { type: 'price'; label: string; value: string }
  | { type: 'section'; text: string };

interface Plan {
  name: string;
  label: string;
  price: string;
  period: string;
  description: string;
  icon: string;
  iconBg: string;
  features: Feature[];
  highlight: boolean;
  badge?: string;
}

export default function Savings() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const plans: Plan[] = [
    {
      name: 'PLANO PRATA',
      label: 'Prata',
      price: '29,90',
      period: 'mês',
      description: 'Saúde acessível com descontos reais no dia a dia.',
      icon: 'ri-medal-line',
      iconBg: 'bg-gradient-to-br from-slate-400 to-slate-500',
      highlight: false,
      features: [
        { type: 'item', text: 'Rede com +3.000 parceiros no Brasil' },
        { type: 'item', text: 'Descontos em farmácias, mercados e serviços' },
        { type: 'item', text: 'Cashback em compras' },
        { type: 'item', text: 'Descontos em viagens' },
        { type: 'item', text: 'Uso imediato, sem carência' },
        { type: 'section', text: 'Consultas online' },
        { type: 'price', label: 'Clínico geral', value: 'R$ 59,90' },
        { type: 'price', label: 'Especialista', value: 'R$ 129,90' },
        { type: 'price', label: 'Nutrição e psicologia', value: 'a partir de R$ 59,90' },
      ],
    },
    {
      name: 'PLANO OURO',
      label: 'Ouro',
      price: '49,90',
      period: 'mês',
      description: 'Mais benefícios com 1 consulta gratuita todo mês.',
      icon: 'ri-medal-fill',
      iconBg: 'bg-gradient-to-br from-yellow-400 to-amber-500',
      highlight: true,
      badge: 'Mais Popular',
      features: [
        { type: 'inherited', text: 'Tudo do Plano Prata' },
        { type: 'free', text: '1 consulta online gratuita/mês com clínico geral' },
        { type: 'item', text: 'Acesso ampliado a benefícios e ofertas' },
      ],
    },
    {
      name: 'PLANO DIAMANTE',
      label: 'Diamante',
      price: '89,90',
      period: 'mês',
      description: 'O máximo em cobertura, com consultas gratuitas todo mês.',
      icon: 'ri-vip-diamond-fill',
      iconBg: 'bg-gradient-to-br from-cyan-400 to-blue-500',
      highlight: false,
      features: [
        { type: 'inherited', text: 'Tudo do Plano Ouro' },
        { type: 'free', text: '5 consultas online gratuitas/mês com clínico geral' },
        { type: 'free', text: '1 consulta online gratuita/mês com especialista' },
        { type: 'free', text: '1 consulta online gratuita/mês com nutricionista' },
        { type: 'free', text: '1 consulta online gratuita/mês com psicóloga' },
        { type: 'item', text: 'Benefícios e condições exclusivas' },
      ],
    },
  ];

  /* WhatsApp empresarial — desativado temporariamente
  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5555997266310?text=Olá! Gostaria de solicitar uma consulta sobre o plano empresarial VitaUni.', '_blank');
  };
  */

  const renderFeature = (feature: Feature, idx: number, highlight: boolean) => {
    if (feature.type === 'section') {
      return (
        <li key={idx} className="flex items-center gap-2 pt-1">
          <i className={`ri-computer-line text-sm ${highlight ? 'text-[#00A8E8]' : 'text-[#00A8E8]'}`}></i>
          <span className={`text-xs font-semibold uppercase tracking-wider ${highlight ? 'text-[#00A8E8]' : 'text-[#00A8E8]'}`}>{feature.text}</span>
        </li>
      );
    }

    if (feature.type === 'price') {
      return (
        <li key={idx} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm ${highlight ? 'bg-white/10' : 'bg-gray-50 border border-gray-100'}`}>
          <span className={highlight ? 'text-gray-300' : 'text-gray-600'}>{feature.label}</span>
          <span className={`font-semibold whitespace-nowrap ${highlight ? 'text-[#00A8E8]' : 'text-[#0A1628]'}`}>{feature.value}</span>
        </li>
      );
    }

    if (feature.type === 'inherited') {
      return (
        <li key={idx} className="flex items-center gap-3">
          <i className={`ri-stack-line text-lg shrink-0 ${highlight ? 'text-[#00A8E8]' : 'text-[#00A8E8]'}`}></i>
          <span className={`font-semibold text-sm ${highlight ? 'text-[#00A8E8]' : 'text-[#00A8E8]'}`}>{feature.text}</span>
        </li>
      );
    }

    if (feature.type === 'free') {
      return (
        <li key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-xl ${highlight ? 'bg-green-500/15' : 'bg-green-50 border border-green-100'}`}>
          <i className={`ri-gift-line text-lg shrink-0 ${highlight ? 'text-green-400' : 'text-green-600'}`}></i>
          <span className={`text-sm font-medium ${highlight ? 'text-green-300' : 'text-green-800'}`}>{feature.text}</span>
        </li>
      );
    }

    return (
      <li key={idx} className="flex items-start gap-3">
        <i className={`ri-check-line text-lg mt-0.5 shrink-0 ${highlight ? 'text-green-400' : 'text-green-600'}`}></i>
        <span className={`text-sm ${highlight ? 'text-gray-200' : 'text-gray-600'}`}>{feature.text}</span>
      </li>
    );
  };

  return (
    <>
      <section id="planos" className="py-24 bg-gray-50 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, #0A1628 1px, transparent 1px)', backgroundSize: '30px 30px' }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#0A1628] mb-4">Planos de Assinatura</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Escolha o plano ideal para você e sua família. A partir de <strong>R$ 29,90/mês</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12 items-start">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-3xl p-8 transition-all duration-300 flex flex-col ${
                  plan.highlight
                    ? 'bg-gradient-to-br from-[#0A1628] to-[#1A0B2E] text-white shadow-2xl scale-105 border-2 border-[#00A8E8]'
                    : 'bg-white border-2 border-gray-200 hover:shadow-xl hover:-translate-y-1'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-[#00A8E8] to-[#E91E63] rounded-full text-sm font-semibold mb-5 self-start">
                    <i className="ri-sparkling-line"></i>
                    {plan.badge}
                  </div>
                )}

                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 ${plan.iconBg} rounded-xl flex items-center justify-center shadow-md shrink-0`}>
                    <i className={`${plan.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className={`text-xl font-bold tracking-wide ${plan.highlight ? 'text-white' : 'text-gray-800'}`}>
                    {plan.name}
                  </h3>
                </div>

                <p className={`text-sm mb-5 leading-relaxed ${plan.highlight ? 'text-gray-300' : 'text-gray-500'}`}>
                  {plan.description}
                </p>

                {/* Price */}
                <div className={`mb-6 pb-6 border-b ${plan.highlight ? 'border-white/10' : 'border-gray-100'}`}>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-sm font-medium ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>R$</span>
                    <span className={`text-5xl font-extrabold tracking-tight ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-base ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>/mês</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, idx) => renderFeature(feature, idx, plan.highlight))}
                </ul>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className={`w-full py-4 rounded-full font-semibold transition-all cursor-pointer text-sm ${
                    plan.highlight
                      ? 'bg-gradient-to-r from-[#00A8E8] to-[#E91E63] text-white hover:shadow-2xl hover:shadow-[#00A8E8]/40'
                      : 'bg-[#0A1628] text-white hover:bg-gradient-to-r hover:from-[#00A8E8] hover:to-[#E91E63] hover:shadow-lg'
                  }`}
                >
                  Baixar App e Assinar
                </button>
              </div>
            ))}
          </div>

          {/* Plano Empresarial — desativado temporariamente
          <div className="bg-gradient-to-r from-[#0A1628] to-[#1A0B2E] rounded-2xl p-8 border-2 border-[#00A8E6]/30 shadow-xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-gradient-to-r from-[#00A8E8] to-[#E91E63] rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                  <i className="ri-building-2-line text-3xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-1">Plano Empresarial</h4>
                  <p className="text-gray-300 text-sm max-w-lg">
                    Leve os benefícios VitaUni para toda a sua equipe. Condições especiais, gestão centralizada e suporte dedicado.
                  </p>
                </div>
              </div>
              <button
                onClick={handleWhatsAppClick}
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-green-500/30 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                Solicitar Consulta
              </button>
            </div>
          </div>
          */}
        </div>
      </section>

      <DownloadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
